import React, { useState } from 'react';

const CampaignSetup = ({ onCampaignReady, userId }) => {
  const [campaignName, setCampaignName] = useState('');
  const [industry, setIndustry] = useState('ecommerce');
  const [targetMarket, setTargetMarket] = useState('United States');
  const [urls, setUrls] = useState(['']);
  const [prompts, setPrompts] = useState(['']);
  const [competitors, setCompetitors] = useState(['']);

  const fontStyle = { fontFamily: "'Work Sans', sans-serif" };

  const addUrlField = () => setUrls([...urls, '']);
  const removeUrlField = (index) => {
    const newUrls = urls.filter((_, i) => i !== index);
    setUrls(newUrls.length === 0 ? [''] : newUrls);
  };
  const updateUrl = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const addPromptField = () => setPrompts([...prompts, '']);
  const removePromptField = (index) => {
    const newPrompts = prompts.filter((_, i) => i !== index);
    setPrompts(newPrompts.length === 0 ? [''] : newPrompts);
  };
  const updatePrompt = (index, value) => {
    const newPrompts = [...prompts];
    newPrompts[index] = value;
    setPrompts(newPrompts);
  };

  const addCompetitorField = () => setCompetitors([...competitors, '']);
  const removeCompetitorField = (index) => {
    const newCompetitors = competitors.filter((_, i) => i !== index);
    setCompetitors(newCompetitors.length === 0 ? [''] : newCompetitors);
  };
  const updateCompetitor = (index, value) => {
    const newCompetitors = [...competitors];
    newCompetitors[index] = value;
    setCompetitors(newCompetitors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validUrls = urls.filter(url => url.trim() !== '');
    const validPrompts = prompts.filter(prompt => prompt.trim() !== '');
    const validCompetitors = competitors.filter(comp => comp.trim() !== '');

    if (!campaignName.trim()) {
      alert('Please enter a campaign name');
      return;
    }

    if (validUrls.length === 0) {
      alert('Please enter at least one URL');
      return;
    }

    // Safety guard: if userId is missing, refuse to submit rather than
    // silently creating an orphaned campaign with user_id = null.
    if (!userId) {
      alert('User identity could not be determined. Please refresh the page and try again. If the problem persists, please log out and back in.');
      console.error('CampaignSetup: handleSubmit called with no userId prop. Cannot create campaign — would result in orphaned record.');
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app';

      const response = await fetch(`${API_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId
        },
        body: JSON.stringify({
          name: campaignName.trim(),
          industry: industry,
          targetMarket: targetMarket,
          urls: validUrls,
          prompts: validPrompts,
          competitors: validCompetitors
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create campaign: ${response.statusText}`);
      }

      const campaign = await response.json();

      const campaignData = {
        id: campaign.id,
        userId: campaign.user_id,
        name: campaign.name,
        industry: campaign.industry,
        targetMarket: campaign.target_market || 'United States',
        urls: campaign.urls,
        prompts: campaign.prompts || [],
        competitors: campaign.competitors || [],
        status: campaign.status,
        createdAt: campaign.created_at,
        updatedAt: campaign.updated_at
      };

      localStorage.setItem('currentCampaign', JSON.stringify(campaignData));
      onCampaignReady(campaignData);
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert(`Failed to create campaign: ${error.message}`);
    }
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
    ...fontStyle
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    ...fontStyle
  };

  const selectStyle = {
    ...inputStyle,
    backgroundColor: 'white'
  };

  const hintStyle = {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '6px',
    ...fontStyle
  };

  const addButtonStyle = {
    padding: '10px 16px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    ...fontStyle
  };

  const removeButtonStyle = {
    padding: '10px 16px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    ...fontStyle
  };

  const rowStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  };

  const sectionStyle = { marginBottom: '24px' };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 24px', ...fontStyle }}>
      <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: '768px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '32px'
        }}>
          <h1 style={{ fontSize: '30px', fontWeight: '700', color: '#111827', marginBottom: '8px', ...fontStyle }}>
            Create New Campaign
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '32px', ...fontStyle }}>
            Add your industry, prompts and URLs to begin your GEO audit
          </p>

          <form onSubmit={handleSubmit}>

            {/* Campaign Name */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Campaign Name</label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                style={inputStyle}
                placeholder="e.g., Q4 Product Pages Audit"
              />
            </div>

            {/* Industry */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Industry</label>
              <select value={industry} onChange={(e) => setIndustry(e.target.value)} style={selectStyle}>
                <option value="b2b_saas">B2B / SaaS</option>
                <option value="charity">Charity / Third Sector</option>
                <option value="ecommerce">E-commerce</option>
                <option value="education">Education</option>
                <option value="financial_services">Financial Services</option>
                <option value="health_wellness">Healthcare & Wellness</option>
                <option value="hospitality">Hospitality</option>
                <option value="marketing_agency">Marketing Agency</option>
                <option value="professional_services">Professional Services</option>
                <option value="real_estate">Real Estate</option>
                <option value="technology_services">Technology Services</option>
                <option value="travel_holiday_rentals">Travel & Holiday Rentals</option>
              </select>
              <p style={hintStyle}>Used to tailor schema requirements and entity classification for your industry</p>
            </div>

            {/* Target Market */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Target Market</label>
              <select value={targetMarket} onChange={(e) => setTargetMarket(e.target.value)} style={selectStyle}>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Spain">Spain</option>
                <option value="Italy">Italy</option>
                <option value="Netherlands">Netherlands</option>
                <option value="Japan">Japan</option>
                <option value="Singapore">Singapore</option>
                <option value="India">India</option>
              </select>
              <p style={hintStyle}>AI search queries will be targeted to this geographic market</p>
            </div>

            {/* Prompts to Audit */}
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Prompts to Audit <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(Optional)</span>
              </label>
              <p style={{ ...hintStyle, marginTop: 0, marginBottom: '12px' }}>
                Enter questions or search queries to test in AI search engines (ChatGPT, Claude, Perplexity)
              </p>
              {prompts.map((prompt, index) => (
                <div key={index} style={rowStyle}>
                  <input
                    type="text"
                    value={prompt}
                    onChange={(e) => updatePrompt(index, e.target.value)}
                    style={{ ...inputStyle, flex: 1, width: 'auto' }}
                    placeholder="e.g., What's the best collagen supplement for joint health?"
                  />
                  {prompts.length > 1 && (
                    <button type="button" onClick={() => removePromptField(index)} style={removeButtonStyle}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addPromptField} style={addButtonStyle}>
                + Add Another Prompt
              </button>
            </div>

            {/* URLs to Audit */}
            <div style={sectionStyle}>
              <label style={labelStyle}>URLs to Audit</label>
              {urls.map((url, index) => (
                <div key={index} style={rowStyle}>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                    style={{ ...inputStyle, flex: 1, width: 'auto' }}
                    placeholder="https://example.com/page"
                  />
                  {urls.length > 1 && (
                    <button type="button" onClick={() => removeUrlField(index)} style={removeButtonStyle}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addUrlField} style={addButtonStyle}>
                + Add Another URL
              </button>
            </div>

            {/* Competitor URLs */}
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Competitor URLs <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(Optional)</span>
              </label>
              <p style={{ ...hintStyle, marginTop: 0, marginBottom: '12px' }}>
                Add competitor websites or brand names to benchmark your performance against
              </p>
              {competitors.map((competitor, index) => (
                <div key={index} style={rowStyle}>
                  <input
                    type="text"
                    value={competitor}
                    onChange={(e) => updateCompetitor(index, e.target.value)}
                    style={{ ...inputStyle, flex: 1, width: 'auto' }}
                    placeholder="Apple or https://www.apple.com"
                  />
                  {competitors.length > 1 && (
                    <button type="button" onClick={() => removeCompetitorField(index)} style={removeButtonStyle}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addCompetitorField} style={addButtonStyle}>
                + Add Another Competitor
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#048ba8',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                ...fontStyle
              }}
            >
              Create Campaign
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignSetup;
